import type { VtPayload } from "./payload.js";

export type VtSectionId =
  | "general"
  | "location"
  | "access"
  | "terrain"
  | "extension"
  | "utilities"
  | "artisan"
  | "clientWorks"
  | "remarks"
  | "signatures"
  | "internal";

export type VtStep = {
  /** URL-safe section slug. */
  section: VtSectionId;
  /** URL-safe sub-section slug. */
  subsection: string;
  /** Human label (FR) shown in headers and progress recaps. */
  label: string;
  /** Optional gate predicate; the step is hidden when the predicate returns false. */
  conditionalOn?: (payload: VtPayload) => boolean;
};

/**
 * Ordered list of wizard pages. Adding/removing entries here adjusts the wizard
 * without touching individual page components.
 */
export const VT_STEPS: readonly VtStep[] = [
  // General
  { section: "general", subsection: "info", label: "Informations" },
  { section: "general", subsection: "contact", label: "Gestionnaire" },
  { section: "general", subsection: "remarks", label: "Remarques générales" },

  // Location
  { section: "location", subsection: "gps", label: "Position GPS" },
  { section: "location", subsection: "extras", label: "Kit grand froid" },

  // Access
  { section: "access", subsection: "truck", label: "Type de camion" },
  { section: "access", subsection: "voirie", label: "Voirie & déchargement" },
  { section: "access", subsection: "photos", label: "Photos d'accès" },
  {
    section: "access",
    subsection: "grue",
    label: "Grue",
    conditionalOn: (p) => p.terrain?.needMachines === true,
  },
  { section: "access", subsection: "remarks", label: "Remarques accès" },

  // Terrain
  { section: "terrain", subsection: "stockage", label: "Stockage chantier" },
  { section: "terrain", subsection: "parcelle", label: "Parcelle nue" },
  // Pente avant sol : la matrice fondation dépend de la pente renseignée.
  { section: "terrain", subsection: "pente", label: "Pente du terrain" },
  { section: "terrain", subsection: "sol", label: "Sol & fondation" },
  { section: "terrain", subsection: "excavation", label: "Décaissé" },
  { section: "terrain", subsection: "implantation", label: "Implantation (photos)" },
  { section: "terrain", subsection: "remarks", label: "Remarques terrain" },

  // Extension
  { section: "extension", subsection: "gate", label: "Extension de maison ?" },
  {
    section: "extension",
    subsection: "toiture",
    label: "Toiture",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },
  {
    section: "extension",
    subsection: "altimetrie",
    label: "Altimétrie",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },
  {
    section: "extension",
    subsection: "facade",
    label: "Façade & ouverture",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },
  {
    section: "extension",
    subsection: "solin",
    label: "Solin",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },
  {
    section: "extension",
    subsection: "vmc",
    label: "VMC",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },
  {
    section: "extension",
    subsection: "remarks",
    label: "Remarques extension",
    conditionalOn: (p) => p.extension?.isExtensionHouse === true,
  },

  // Utilities
  { section: "utilities", subsection: "gas", label: "Gaz" },
  { section: "utilities", subsection: "gate", label: "Raccordements & réseaux" },
  {
    section: "utilities",
    subsection: "electrical",
    label: "Électricité",
    conditionalOn: (p) => p.utilities?.requiredConnections?.includes("electrical") === true,
  },
  {
    section: "utilities",
    subsection: "water",
    label: "Eau potable",
    conditionalOn: (p) => p.utilities?.requiredConnections?.includes("water") === true,
  },
  {
    section: "utilities",
    subsection: "wastewater",
    label: "Eaux usées",
    conditionalOn: (p) => p.utilities?.requiredConnections?.includes("wastewater") === true,
  },
  {
    section: "utilities",
    subsection: "schemas",
    label: "Plan des raccordements",
    conditionalOn: (p) => p.utilities?.hasConnections === true,
  },
  {
    section: "utilities",
    subsection: "remarks",
    label: "Remarques réseaux",
    conditionalOn: (p) => p.utilities?.hasConnections === true,
  },

  // Engins (fin de rapport, entre réseaux et artisans)
  { section: "terrain", subsection: "engins", label: "Engins" },

  // Artisan
  { section: "artisan", subsection: "select", label: "Artisans à consulter" },

  // Client works
  { section: "clientWorks", subsection: "tasks", label: "Travaux à réaliser" },
  { section: "clientWorks", subsection: "validation", label: "Non-conformités" },
  { section: "clientWorks", subsection: "budget", label: "Budget upsell / downsell" },
  { section: "clientWorks", subsection: "requests", label: "Demandes client" },
  { section: "clientWorks", subsection: "waste", label: "Gestion des déchets" },

  // Global remarks
  { section: "remarks", subsection: "global", label: "Observations globales" },

  // Signatures
  { section: "signatures", subsection: "conducteur", label: "Signature conducteur" },
  { section: "signatures", subsection: "client", label: "Signature client" },

  // Annotation interne (hors rapport client)
  { section: "internal", subsection: "observations", label: "Annotation interne" },
] as const;

export function findVtStep(
  section: string,
  subsection: string,
): VtStep | undefined {
  return VT_STEPS.find(
    (step) => step.section === section && step.subsection === subsection,
  );
}

export function visibleVtSteps(payload: VtPayload): VtStep[] {
  return VT_STEPS.filter((step) =>
    step.conditionalOn ? step.conditionalOn(payload) : true,
  );
}

export function nextVtStep(
  current: VtStep,
  payload: VtPayload,
): VtStep | undefined {
  const visible = visibleVtSteps(payload);
  const idx = visible.findIndex(
    (s) => s.section === current.section && s.subsection === current.subsection,
  );
  if (idx === -1) return undefined;
  return visible[idx + 1];
}

export function prevVtStep(
  current: VtStep,
  payload: VtPayload,
): VtStep | undefined {
  const visible = visibleVtSteps(payload);
  const idx = visible.findIndex(
    (s) => s.section === current.section && s.subsection === current.subsection,
  );
  if (idx <= 0) return undefined;
  return visible[idx - 1];
}
